import { Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, finalize, map, startWith } from 'rxjs';
import { BaseTableComponent } from '../../shared/base/base-table.component';
import { formatHttpErrorMessage } from '../../shared/utils/error.utils';
import { FileMovementLog } from '../../models/file-movement-log.model';
import { FileMovementLogsService } from '../../services/file-movement-logs.service';

@Component({
  selector: 'app-file-movement-logs',
  standalone: false,
  templateUrl: './file-movement-logs.component.html',
  styleUrls: ['./file-movement-logs.component.css'],
})
export class FileMovementLogsComponent extends BaseTableComponent<FileMovementLog> {
  override sortBy = 'date';
  override sortDir: 'asc' | 'desc' = 'desc';

  readonly displayedColumns = [
    's3Bucket',
    'inputFilePath',
    'client',
    'sourcePath',
    'destinationPath',
    'status',
    'statusDesc',
    'date',
  ];

  readonly statuses = ['SUCCESS', 'FAILED'];
  readonly statusControl = new FormControl('', { nonNullable: true });
  filteredStatuses$: Observable<string[]> =
    this.statusControl.valueChanges.pipe(
      startWith(this.statusControl.value),
      map((value) =>
        this.statuses.filter((status) =>
          status.toLowerCase().includes(value.trim().toLowerCase()),
        ),
      ),
    );

  filter = {
    client: '',
    status: '',
    fromDate: null as Date | null,
    toDate: null as Date | null,
  };

  constructor(private fileMovementLogsService: FileMovementLogsService) {
    super();
  }

  protected override loadData(skip: number, count: number): void {
    this.loading = true;
    this.fileMovementLogsService
      .getLogs(skip, count, this.sortBy, this.sortDir, {
        client: this.filter.client,
        status: this.filter.status,
        fromDate: this.toApiDate(this.filter.fromDate),
        toDate: this.toApiDate(this.filter.toDate),
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          this.dataSource.data = response.logs;
          this.totalCount = response.totalCount;
          this.errorMessage = '';
        },
        error: (err) => {
          this.errorMessage = formatHttpErrorMessage(
            'Failed to load file movement logs',
            err,
          );
          this.dataSource.data = [];
          this.totalCount = 0;
        },
      });
  }

  onFilter(): void {
    this.filter.status = this.normalizeStatus(this.statusControl.value);
    this.statusControl.setValue(this.filter.status, { emitEvent: false });
    this.pageIndex = 0;
    this.loadDataWrapper();
  }

  clearFilters(): void {
    this.filter = { client: '', status: '', fromDate: null, toDate: null };
    this.statusControl.setValue('', { emitEvent: false });
    this.pageIndex = 0;
    this.loadDataWrapper();
  }

  displayStatus(value: string | null): string {
    return value ?? '';
  }

  private normalizeStatus(value: string): string {
    const normalized = value.trim().toLowerCase();
    return (
      this.statuses.find((status) => status.toLowerCase() === normalized) ?? ''
    );
  }

  private toApiDate(value: Date | null): string {
    if (!value) return '';
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
